import React, { useEffect, useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Badge, Flex, Text, Box, HStack, Select, Input,
  Tooltip, Progress,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioPlanCultura from './FormularioPlanCultura';

const ESTATUS_COLORS: Record<string, string> = {
  Borrador: 'gray',
  Activo: 'green',
  Concluido: 'blue',
};

const TablaPlanAnualCultura: React.FC = () => {
  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtroAnio, setFiltroAnio] = useState<string>('');
  const [filtroEstatus, setFiltroEstatus] = useState<string>('');
  const [busqueda, setBusqueda] = useState<string>('');
  const itemsPerPage = 10;

  const anioActual = new Date().getFullYear();
  const aniosDisponibles = Array.from({ length: 8 }, (_, i) => anioActual - 2 + i);

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await axiosInstance.get('/cultura/plan-anual');
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtrado
  const datosFiltrados = data.filter((item) => {
    const matchAnio = filtroAnio ? item.anio?.toString() === filtroAnio : true;
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchBusqueda = busqueda
      ? item.nombrePlan?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.responsable?.toLowerCase().includes(busqueda.toLowerCase())
      : true;
    return matchAnio && matchEstatus && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleVer = (item: any) => {
    setSelectedItem(item);
    onOpenVer();
  };

  const handleEditar = (item: any) => {
    setSelectedItem(item);
    onOpenEditar();
  };

  const handleGuardado = () => {
    onCloseNuevo();
    onCloseEditar();
    fetchData();
  };

  // Exportar Excel
  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Año': item.anio,
      'Nombre del Plan': item.nombrePlan,
      'Fecha Inicio': item.fechaInicio ? new Date(item.fechaInicio).toLocaleDateString('es-MX') : '',
      'Fecha Fin': item.fechaFin ? new Date(item.fechaFin).toLocaleDateString('es-MX') : '',
      'Responsable': item.responsable,
      '% Avance': `${item.porcentajeAvance || 0}%`,
      'Estatus': item.estatus,
    }));

    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Planes Culturales');
    XLSX.writeFile(wb, `Planes_Culturales_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Exportar PDF
  const handleExportPDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    doc.setFontSize(16);
    doc.text('Planeación Cultural - Planes Anuales', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-MX')}`, 14, 22);

    autoTable(doc, {
      startY: 27,
      head: [['Año', 'Nombre del Plan', 'Fecha Inicio', 'Fecha Fin', 'Responsable', '% Avance', 'Estatus']],
      body: datosFiltrados.map((item) => [
        item.anio,
        item.nombrePlan,
        item.fechaInicio ? new Date(item.fechaInicio).toLocaleDateString('es-MX') : '',
        item.fechaFin ? new Date(item.fechaFin).toLocaleDateString('es-MX') : '',
        item.responsable,
        `${item.porcentajeAvance || 0}%`,
        item.estatus,
      ]),
      headStyles: { fillColor: [161, 38, 61] },
      styles: { fontSize: 9 },
    });

    doc.save(`Planes_Culturales_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const formatFecha = (fecha: string) =>
    fecha ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  return (
    <Box>
      {/* Barra de herramientas */}
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por nombre o responsable..."
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
            size="sm"
            w={{ base: 'full', md: '220px' }}
          />
          <Select
            placeholder="Filtrar por año"
            value={filtroAnio}
            onChange={(e) => { setFiltroAnio(e.target.value); setCurrentPage(1); }}
            size="sm"
            w="150px"
          >
            {aniosDisponibles.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Select>
          <Select
            placeholder="Filtrar por estatus"
            value={filtroEstatus}
            onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }}
            size="sm"
            w="160px"
          >
            <option value="Borrador">Borrador</option>
            <option value="Activo">Activo</option>
            <option value="Concluido">Concluido</option>
          </Select>
        </HStack>

        <HStack spacing={2}>
          <Tooltip label="Exportar a Excel">
            <Button
              size="sm"
              leftIcon={<DownloadIcon />}
              colorScheme="green"
              variant="outline"
              onClick={handleExportExcel}
            >
              Excel
            </Button>
          </Tooltip>
          <Tooltip label="Exportar a PDF">
            <Button
              size="sm"
              leftIcon={<DownloadIcon />}
              colorScheme="red"
              variant="outline"
              onClick={handleExportPDF}
            >
              PDF
            </Button>
          </Tooltip>
          <Button
            size="sm"
            leftIcon={<AddIcon />}
            bg="#5b182b"
            color="white"
            _hover={{ bg: '#691f30' }}
            onClick={onOpenNuevo}
          >
            Nuevo Plan
          </Button>
        </HStack>
      </Flex>

      {/* Tabla */}
      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" textTransform="uppercase" fontSize="xs">Año</Th>
              <Th color="white" textTransform="uppercase" fontSize="xs">Nombre del Plan</Th>
              <Th color="white" textTransform="uppercase" fontSize="xs">Fecha Inicio</Th>
              <Th color="white" textTransform="uppercase" fontSize="xs">Fecha Fin</Th>
              <Th color="white" textTransform="uppercase" fontSize="xs">Responsable</Th>
              <Th color="white" textTransform="uppercase" fontSize="xs" isNumeric>% Avance</Th>
              <Th color="white" textTransform="uppercase" fontSize="xs">Estatus</Th>
              <Th color="white" textTransform="uppercase" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>
                  <Text color="gray.500">Cargando...</Text>
                </Td>
              </Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>
                  <Text color="gray.400">No se encontraron registros</Text>
                </Td>
              </Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td fontWeight="semibold">{item.anio}</Td>
                  <Td maxW="240px">
                    <Text noOfLines={1} title={item.nombrePlan}>{item.nombrePlan}</Text>
                  </Td>
                  <Td>{formatFecha(item.fechaInicio)}</Td>
                  <Td>{formatFecha(item.fechaFin)}</Td>
                  <Td>{item.responsable}</Td>
                  <Td isNumeric>
                    <Box>
                      <Text fontSize="xs" mb={1} textAlign="right">{item.porcentajeAvance || 0}%</Text>
                      <Progress
                        value={item.porcentajeAvance || 0}
                        size="xs"
                        colorScheme={
                          (item.porcentajeAvance || 0) >= 100 ? 'blue' :
                          (item.porcentajeAvance || 0) >= 50 ? 'green' : 'orange'
                        }
                        borderRadius="full"
                        w="80px"
                        ml="auto"
                      />
                    </Box>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={ESTATUS_COLORS[item.estatus] || 'gray'}
                      fontSize="xs"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                    >
                      {item.estatus}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => handleVer(item)}
                      >
                        Ver
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        variant="outline"
                        onClick={() => handleEditar(item)}
                      >
                        Editar
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Paginación */}
      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">
            Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length} registros
          </Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button
                  key={page}
                  size="xs"
                  bg={currentPage === page ? '#5b182b' : undefined}
                  color={currentPage === page ? 'white' : undefined}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Modal: Ver */}
      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Detalle del Plan Cultural
          </ModalHeader>
          <ModalBody py={5}>
            {selectedItem && <FormularioPlanCultura modo="view" data={selectedItem} />}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCloseVer}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: Editar */}
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Editar Plan Cultural
          </ModalHeader>
          <ModalBody py={5}>
            {selectedItem && (
              <FormularioPlanCultura
                modo="edit"
                data={selectedItem}
                onGuardado={handleGuardado}
                onCancelar={onCloseEditar}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal: Nuevo */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Nuevo Plan Cultural
          </ModalHeader>
          <ModalBody py={5}>
            <FormularioPlanCultura
              modo="new"
              onGuardado={handleGuardado}
              onCancelar={onCloseNuevo}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TablaPlanAnualCultura;
